import { useRef } from 'react';
import styles from '../ScenarioGenerator.module.css';
import { Tooltip } from '../../../common/Tooltip';
import { SingleSelectDropdown } from '../../../common/SingleSelectDropdown';
import { SeedancePresetActorPicker } from '../SeedancePresetActorPicker';
import type { SetupPanelProps } from '../SetupPanel';
import { useScenarioSetup } from '../hooks/ScenarioSetupContext';
import type { CastingActor } from '../../state/useGenerationState';
import { castingCapFromCharacterCount, modelCastActorCap, filterCastingModelsForDisplay } from '../utils';
import { useGenerationModels } from '../../../../features/generation-models/hooks';
import { useToast } from '../../../../hooks/useToast';
import { isCharacterActor } from '../../../../features/actors/actorAttributes';

export function CastingSection(props: SetupPanelProps) {
  const { accordions } = props;

  const {
    videoSettings,
    selectedCasting, setSelectedCasting,
    additionalCastings, setAdditionalCastings,
    markCastingExplicit,
    castingGenderFilter, setCastingGenderFilter, castingAgeFilter, setCastingAgeFilter,
    castingEthnicityFilter, setCastingEthnicityFilter,
    excludePeople, setExcludePeople,
    castingModelsQuery,
    selectedSeedancePresetActor, setSelectedSeedancePresetActor,
    additionalSeedancePresetActors, setAdditionalSeedancePresetActors,
    characterCount, setCharacterCount, characterCountSelectOptions, characterCountTreeEnabled,
    visualStyleIsStylized } = useScenarioSetup();

  const toast = useToast();
  const { data: videoModels = [] } = useGenerationModels('video');

  // 複数AI俳優対応: 選択上限 = min(登場人物数, モデル別キャスト上限)。人数未指定時は 1。
  // 匿名の登場人物は参照画像枠を消費しないため人数ドロップダウン自体はモデル非依存だが、
  // 俳優カードは1人1枚参照枠を使うので参照枠の小さいモデル (MiniMax 等) で先に頭打ちになる。
  const characterCountCap = castingCapFromCharacterCount(characterCount, characterCountSelectOptions);
  const modelCap = modelCastActorCap(videoSettings.videoModel, videoModels);
  const castingCap = Math.min(characterCountCap, modelCap);
  // モデル側の上限が効いているか (文言の出し分けに使う)
  const isModelCapBinding = modelCap < characterCountCap;
  // フルキャスト = 主俳優(selectedCasting=登場人物A) + 追加俳優(additionalCastings)。
  const fullCast: CastingActor[] = [
    ...(selectedCasting ? [selectedCasting] : []),
    ...additionalCastings,
  ];
  const fullCastRef = useRef<CastingActor[]>(fullCast);
  fullCastRef.current = fullCast;
  const selectedCastingIds = new Set(fullCast.map((c) => c.id));
  const castingOrderById = new Map(fullCast.map((c, i) => [c.id, i]));
  const isMultiCastingEnabled = castingCap > 1;

  const toCastingActor = (model: {
    id: string; display_name: string;
    face_image_public_url?: string; character_sheet_public_url?: string;
    gender?: string; min_age?: number; max_age?: number; ethnicity?: string;
  }): CastingActor => ({
    id: model.id,
    display_name: model.display_name,
    face_image_public_url: model.face_image_public_url,
    character_sheet_public_url: model.character_sheet_public_url,
    gender: model.gender,
    min_age: model.min_age,
    max_age: model.max_age,
    ethnicity: model.ethnicity,
  });

  // 俳優カードのトグル。cap<=1 は従来どおりの単数置換 (後方互換)。
  const handleToggleCasting = (model: Parameters<typeof toCastingActor>[0]) => {
    const currentFullCast = fullCastRef.current;
    const applyFullCast = (next: CastingActor[]) => {
      fullCastRef.current = next;
      const [primary, ...additional] = next;
      setSelectedCasting(primary ?? null);
      setAdditionalCastings(additional);
      // ユーザー操作によるキャスト編集: DB復元 (inherited) の上書き対象から外す
      // (docs/features/character-casting-consistency-plan.md 修正2-a)。
      markCastingExplicit();
    };
    const id = model.id;
    if (currentFullCast.some((c) => c.id === id)) {
      // 選択解除。主俳優を外す場合は追加俳優の先頭を主に昇格。
      applyFullCast(currentFullCast.filter((c) => c.id !== id));
      return;
    }
    const nextActor = toCastingActor(model);
    if (castingCap <= 1) {
      applyFullCast([nextActor]);
      return;
    }
    if (currentFullCast.length >= castingCap) {
      toast.error(isModelCapBinding
        ? `選択中の動画モデルで参照できる俳優の上限（${castingCap}人）に達しています`
        : `登場人物数の上限（${castingCap}人）に達しています`);
      return;
    }
    applyFullCast([...currentFullCast, nextActor]);
  };

  const handleClearCasting = () => {
    fullCastRef.current = [];
    setSelectedCasting(null);
    setAdditionalCastings([]);
    markCastingExplicit();
  };

  // --- Seedance preset 版 (上限・選択順ロジックは casting と同じ) ---
  const fullPresetCast = [
    ...(selectedSeedancePresetActor ? [selectedSeedancePresetActor] : []),
    ...additionalSeedancePresetActors,
  ];
  const fullPresetCastRef = useRef(fullPresetCast);
  fullPresetCastRef.current = fullPresetCast;
  const selectedPresetIds = new Set(fullPresetCast.map((a) => a.id));
  const presetOrderById = new Map(fullPresetCast.map((a, i) => [a.id, i]));

  const handleTogglePresetActor = (actor: typeof additionalSeedancePresetActors[number]) => {
    const currentFullPresetCast = fullPresetCastRef.current;
    const applyFullPresetCast = (next: typeof fullPresetCast) => {
      fullPresetCastRef.current = next;
      const [primary, ...additional] = next;
      setSelectedSeedancePresetActor(primary ?? null);
      setAdditionalSeedancePresetActors(additional);
      // Seedance プリセット選択もキャスティング編集操作の一種として explicit へ遷移させる
      // (復元対象は selectedCasting/additionalCastings のみだが、遷移トリガーには含める)。
      markCastingExplicit();
    };
    const id = actor.id;
    if (currentFullPresetCast.some((a) => a.id === id)) {
      applyFullPresetCast(currentFullPresetCast.filter((a) => a.id !== id));
      return;
    }
    if (castingCap <= 1) {
      applyFullPresetCast([actor]);
      return;
    }
    if (currentFullPresetCast.length >= castingCap) {
      toast.error(isModelCapBinding
        ? `選択中の動画モデルで参照できる俳優の上限（${castingCap}人）に達しています`
        : `登場人物数の上限（${castingCap}人）に達しています`);
      return;
    }
    applyFullPresetCast([...currentFullPresetCast, actor]);
  };

  const handleClearPresetActors = () => {
    fullPresetCastRef.current = [];
    setSelectedSeedancePresetActor(null);
    setAdditionalSeedancePresetActors([]);
    markCastingExplicit();
  };

  // Seedance: プリセット (BytePlus) とキャラクター俳優 (ethnicity='character' の専属AI俳優)
  // は排他 (計画書§4 P4-4・2026-08-19決定済み)。片方を選んだらもう片方の選択を解除する。
  const handleToggleSeedancePresetActor = (actor: typeof additionalSeedancePresetActors[number]) => {
    if (fullCastRef.current.length > 0) {
      handleClearCasting();
    }
    handleTogglePresetActor(actor);
  };

  const handleToggleSeedanceCharacterActor = (model: Parameters<typeof toCastingActor>[0]) => {
    if (fullPresetCastRef.current.length > 0) {
      handleClearPresetActors();
    }
    handleToggleCasting(model);
  };

  // 非写実(アニメ/イラスト/グラフィック)系では写実前提の人間 AI俳優/Seedance プリセットを
  // 選べない。キャラクター俳優 (ethnicity='character') は全スタイルで選択可 (計画書§4 P5-1・
  // 決定11)。「人物を含めない」は従来どおりセクション全体を無効化する。
  // stylized 判定は DB の visual_family 由来 (OEM 独自スタイル対応)。
  const isStylized = visualStyleIsStylized;

  // Seedance 動画モデル選択時は BytePlus preset ピッカーとキャラクター俳優
  // (ethnicity='character') ピッカーを人種セグメント (seedanceView) で切り替えて
  // どちらか一方だけ表示する。Seedance は人物顔を含む参照画像が制限される
  // (face_upload_restricted=true) ため人間俳優の CS は使えないが、キャラクター俳優は
  // 実在顔を含まないためこの制限の対象外 (計画書§4 P4。face-safe 検査を通った個体のみ
  // BE が実際の添付を許可する。fail-close はサーバー側)。プリセットは従来どおり
  // 写実前提のため isStylized では選べない。
  const isSeedanceVideoModel = (videoSettings.videoModel || '').startsWith('seedance');
  const castingDisabled = excludePeople;

  // Seedance では人種セグメントを「人物 (BytePlus プリセット) / キャラクター
  // (キャラクター俳優)」の 2 択スイッチとして流用する (指定なし/外国人は出さない。
  // 内部値は他モデルの人種フィルタと共有するため 'jp' を流用する)。
  // 見た目は他モデルの人種フィルタと同一だが、裏側は表示するピッカーの切替であり、
  // ピッカーは常にどちらか一方だけを表示する (2段並びにしない)。
  // 非写実系はプリセット (写実前提) を選べないためキャラクター側に固定する。
  const seedanceView: 'jp' | 'character' = (() => {
    if (visualStyleIsStylized) return 'character';
    if (castingEthnicityFilter === 'character') return 'character';
    if (castingEthnicityFilter === 'jp') return 'jp';
    // 明示切替前 (「指定なし」「外国人」のまま Seedance へ切り替えた等) は
    // 現在の選択状態から推定する。キャラクター俳優のみ選択中ならキャラクター側。
    if (fullCast.length > 0 && fullPresetCast.length === 0) return 'character';
    return 'jp';
  })();

  const {
    isCastingSectionOpen, setIsCastingSectionOpen } = accordions;

  // 非写実スタイル×AI俳優 (非Seedance) では、俳優一覧をキャラクター俳優のみに絞り込む
  // (人間俳優は非表示。ゲート自体は castingDisabled ではなく一覧のフィルタで実現する)。
  // 人種フィルタ (castingEthnicityFilter) と、年代フィルタ有効時のキャラクター俳優除外
  // (ExclusiveActorsPage と同じ規約) はこの AI俳優一覧にのみ適用する。isStylized の
  // キャラクター専用一覧は人種の軸自体が無意味なので年代フィルタでは絞り込まない。
  const castingModelsForDisplay = (isStylized && !isSeedanceVideoModel)
    ? filterCastingModelsForDisplay(castingModelsQuery.data?.filter(isCharacterActor), castingEthnicityFilter, null)
    : filterCastingModelsForDisplay(castingModelsQuery.data, castingEthnicityFilter, castingAgeFilter);

  // Seedance のキャラクター俳優ピッカー専用の一覧。プリセットとは独立して、
  // スタイルを問わず常にキャラクター俳優のみに絞り込む。
  const characterActorsForSeedance = castingModelsQuery.data?.filter(isCharacterActor);

  // キャスティングアコーディオンのサマリ表示。
  // 優先順: 人物を含めない > Seedance プリセット/キャラクター俳優 > 選択済み俳優 >
  // 性別/年代フィルタ > 指定しない
  const castingSummary = (() => {
    if (excludePeople) return '人物・キャラクターを含めない';
    if (isSeedanceVideoModel) {
      // プリセットとキャラクター俳優は排他なので、選択されている方だけを見ればよい。
      const activeSeedanceCast = fullPresetCast.length > 0 ? fullPresetCast : fullCast;
      if (activeSeedanceCast.length === 1) return activeSeedanceCast[0].display_name;
      if (activeSeedanceCast.length > 1) return `${activeSeedanceCast.length}人選択中`;
    } else if (fullCast.length === 1) {
      return fullCast[0].display_name;
    } else if (fullCast.length > 1) {
      return `${fullCast.length}人選択中`;
    }
    // 俳優/プリセット未選択時は性別・年代フィルタを表示
    const filters: string[] = [];
    if (castingGenderFilter === 'male') filters.push('男性');
    else if (castingGenderFilter === 'female') filters.push('女性');
    if (castingAgeFilter !== null) filters.push(`${castingAgeFilter}歳`);
    if (!isSeedanceVideoModel) {
      if (castingEthnicityFilter === 'jp') filters.push('日本人');
      else if (castingEthnicityFilter === 'foreigner') filters.push('外国人');
      else if (castingEthnicityFilter === 'character') filters.push('キャラクター');
    }
    return filters.length ? filters.join('・') : '指定しない';
  })();

  return (
    <>
          <div className={`accordion-section ${isCastingSectionOpen ? 'accordion-section-open' : ''}`}>
            <button
              type="button"
              className="accordion-header"
              onClick={() => setIsCastingSectionOpen(!isCastingSectionOpen)}
              aria-expanded={isCastingSectionOpen}
            >
              <span className="accordion-title">
                <img src="/single_woman_book.svg" alt="" className="accordion-title-icon" />
                <span>キャスティング</span>
                <span className="accordion-summary">
                  {castingSummary}
                </span>
              </span>
              <span className={`accordion-icon ${isCastingSectionOpen ? 'accordion-icon-open' : ''}`}>
                <svg width="8" height="8" viewBox="0 0 8 8" fill="currentColor" style={{ transition: 'transform 0.2s', transform: isCastingSectionOpen ? 'rotate(90deg)' : 'none' }}><path d="M2 0l4 4-4 4z" /></svg>
              </span>
            </button>
            <div className={`accordion-panel ${isCastingSectionOpen ? 'accordion-panel-open' : 'accordion-panel-closed'}`}>
              {/* 登場人物数 (DB 駆動)。「人物を含めない」ON のときのみ非活性にする
                  (stylized=アニメ系でも人数指定は意味を持つため有効のまま)。
                  将来の複数 AI 俳優対応の入口。flag OFF (tree 無効) のときは行ごと非表示。 */}
              {characterCountTreeEnabled && (
                <div
                  className={styles.scenarioSettingsRow}
                  style={{ marginBottom: 16, ...(excludePeople ? { opacity: 0.4, pointerEvents: 'none' } : {}) }}
                  aria-disabled={excludePeople}
                >
                  <div className={styles.optionGroup}>
                    <div className="gen-option-group-label" style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}>
                      <img src="/multiple_neutral_2.svg" alt="" aria-hidden="true" className={styles.optionLabelIcon} />
                      登場人物数
                    </div>
                    <SingleSelectDropdown
                      label="登場人物数"
                      options={[{ value: '', label: '指定なし' }, ...characterCountSelectOptions]}
                      value={characterCount}
                      onChange={setCharacterCount}
                      defaultValue=""
                      labelOnly
                      fitContent
                      alwaysShowSelected
                      className={`${styles.dropdownFit} ${styles.scenarioDropdown}`}
                      dropdownClassName={styles.dropdownNoWrap}
                    />
                  </div>
                </div>
              )}
              {isStylized && !excludePeople && (
                <div style={{ fontSize: '12px', color: 'rgba(245, 241, 230, 0.6)', marginBottom: 12 }}>
                  {isSeedanceVideoModel
                    ? '選択中の映像スタイル（非写実系）では、「人物」のAI俳優は選べません（キャラクターのみ選択できます）。'
                    : '選択中の映像スタイル（非写実系）では、実写の人間AI俳優は選べません（キャラクター俳優のみ選択できます）。'}
                </div>
              )}
              {/* 「人物を含めない」ON のときのみキャスティング選択を無効化してグレーアウト
                  (フッターのチェックボックスは操作可能なまま残す)。非写実系は無効化せず、
                  Seedance はキャラクター側ビューへ固定 (seedanceView)、非 Seedance は一覧を
                  キャラクター俳優のみに絞り込む (castingModelsForDisplay 参照)。 */}
              <div
                aria-disabled={castingDisabled}
                style={castingDisabled ? { opacity: 0.4, pointerEvents: 'none' } : undefined}
              >
              {/* 性別/年代フィルタ。Seedance プリセットでも AI俳優でも共通なので
                  分岐の外に出して 1 カラム左寄せ (scenarioSettingsRow) で表示する。 */}
              <div className={styles.scenarioSettingsRow} style={{ marginBottom: 16 }}>
                {/* 人種 (キャラクター切替の起点のため性別より先頭に置く)。
                    Seedance では「人物 (プリセット) / キャラクター (キャラクター俳優)」
                    の 2 択スイッチ (seedanceView 参照)。それ以外のモデルでは
                    AI俳優一覧 (castingModelsForDisplay) の絞り込みフィルタ。 */}
                <div className={styles.optionGroup}>
                  <div className="gen-option-group-label">人種</div>
                  <div className={styles.optionGroupButtons}>
                    {isSeedanceVideoModel ? (
                      <>
                        <button
                          type="button"
                          className={`${styles.optionButton} ${seedanceView === 'jp' ? styles.optionButtonActive : ''}`}
                          onClick={() => setCastingEthnicityFilter('jp')}
                          disabled={isStylized}
                          style={isStylized ? { opacity: 0.4, cursor: 'not-allowed' } : undefined}
                        >
                          人物
                        </button>
                        <button
                          type="button"
                          className={`${styles.optionButton} ${seedanceView === 'character' ? styles.optionButtonActive : ''}`}
                          onClick={() => setCastingEthnicityFilter('character')}
                        >
                          キャラクター
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          type="button"
                          className={`${styles.optionButton} ${castingEthnicityFilter === 'none' ? styles.optionButtonActive : ''}`}
                          onClick={() => setCastingEthnicityFilter('none')}
                        >
                          指定なし
                        </button>
                        <button
                          type="button"
                          className={`${styles.optionButton} ${castingEthnicityFilter === 'jp' ? styles.optionButtonActive : ''}`}
                          onClick={() => setCastingEthnicityFilter('jp')}
                        >
                          日本人
                        </button>
                        <button
                          type="button"
                          className={`${styles.optionButton} ${castingEthnicityFilter === 'foreigner' ? styles.optionButtonActive : ''}`}
                          onClick={() => setCastingEthnicityFilter('foreigner')}
                        >
                          外国人
                        </button>
                        <button
                          type="button"
                          className={`${styles.optionButton} ${castingEthnicityFilter === 'character' ? styles.optionButtonActive : ''}`}
                          onClick={() => setCastingEthnicityFilter('character')}
                        >
                          キャラクター
                        </button>
                      </>
                    )}
                  </div>
                </div>
                {/* 性別 */}
                <div className={styles.optionGroup}>
                  <div className="gen-option-group-label">性別</div>
                  <div className={styles.optionGroupButtons}>
                    <button
                      type="button"
                      className={`${styles.optionButton} ${castingGenderFilter === 'none' ? styles.optionButtonActive : ''}`}
                      onClick={() => setCastingGenderFilter('none')}
                    >
                      指定なし
                    </button>
                    <button
                      type="button"
                      className={`${styles.optionButton} ${castingGenderFilter === 'male' ? styles.optionButtonActive : ''}`}
                      onClick={() => setCastingGenderFilter('male')}
                    >
                      男性
                    </button>
                    <button
                      type="button"
                      className={`${styles.optionButton} ${castingGenderFilter === 'female' ? styles.optionButtonActive : ''}`}
                      onClick={() => setCastingGenderFilter('female')}
                    >
                      女性
                    </button>
                  </div>
                </div>
                {/* 年代 */}
                <div className={styles.optionGroup}>
                  <div className="gen-option-group-label">年代</div>
                  <div className={styles.optionGroupButtons}>
                    <button
                      type="button"
                      className={`${styles.optionButton} ${castingAgeFilter === null ? styles.optionButtonActive : ''}`}
                      onClick={() => setCastingAgeFilter(null)}
                    >
                      指定なし
                    </button>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <input
                        type="number"
                        min={10}
                        max={80}
                        value={castingAgeFilter ?? ''}
                        placeholder="30"
                        onChange={(e) => {
                          const next = Number(e.target.value);
                          if (!Number.isNaN(next)) {
                            setCastingAgeFilter(next);
                          }
                        }}
                        onFocus={() => {
                          if (castingAgeFilter === null) {
                            setCastingAgeFilter(30);
                          }
                        }}
                        className={`${styles.ageInput} ${castingAgeFilter !== null ? styles.ageInputActive : ''}`}
                      />
                      <span style={{ color: 'rgba(245, 241, 230, 0.7)', fontSize: '12px' }}>歳</span>
                      <input
                        type="range"
                        min={10}
                        max={80}
                        value={castingAgeFilter ?? 30}
                        onChange={(e) => {
                          const next = Number(e.target.value);
                          if (!Number.isNaN(next)) {
                            setCastingAgeFilter(next);
                          }
                        }}
                        className={`${styles.ageSlider} ${castingAgeFilter !== null ? styles.ageSliderActive : ''}`}
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* カルーセルだけ分岐: Seedance 動画モデルは人種セグメント (seedanceView) で
                  BytePlus プリセットとキャラクター俳優のどちらか一方を表示 (排他選択は
                  handleToggleSeedance* が維持)、それ以外のモデルは AI俳優 */}
              {isSeedanceVideoModel ? (
                seedanceView === 'jp' ? (
                  <>
                  {isMultiCastingEnabled && (
                    <div style={{ fontSize: '12px', color: 'rgba(245, 241, 230, 0.6)', marginBottom: 8 }}>
                      {isModelCapBinding
                        ? `選択中の動画モデルでは最大${castingCap}人まで選択できます（${fullPresetCast.length}/${castingCap}）`
                        : `登場人物数に合わせて最大${castingCap}人まで選択できます（${fullPresetCast.length}/${castingCap}）`}
                    </div>
                  )}
                  <SeedancePresetActorPicker
                    selectedActorIds={selectedPresetIds}
                    orderById={presetOrderById}
                    isMultiEnabled={isMultiCastingEnabled}
                    onToggleActor={handleToggleSeedancePresetActor}
                    onClear={handleClearPresetActors}
                    genderFilter={castingGenderFilter}
                    ageFilter={castingAgeFilter}
                  />
                  </>
                ) : (
                <>
                {/* キャラクター俳優 (ethnicity='character' の専属AI俳優)。プリセットとは排他
                    (計画書§4 P4-4)。見出しは人種セグメントの「キャラクター」が兼ねるため
                    出さない。実際の Seedance 添付可否は face-safe 検査結果に基づき
                    BE が最終判定する (fail-close)。 */}
                {isMultiCastingEnabled && (
                  <div style={{ fontSize: '12px', color: 'rgba(245, 241, 230, 0.6)', marginBottom: 8 }}>
                    {isModelCapBinding
                      ? `選択中の動画モデルでは最大${castingCap}人まで選択できます（${fullCast.length}/${castingCap}）`
                      : `登場人物数に合わせて最大${castingCap}人まで選択できます（${fullCast.length}/${castingCap}）`}
                  </div>
                )}
                <div className={styles.castingCarousel}>
                  <div className={`${styles.castingScrollContainer} ${styles.castingScrollMagnify}`}>
                    <ul className={styles.castingGrid}>
                      <li>
                        <button
                          type="button"
                          className={`${styles.castingCard} ${styles.castingCardNone} ${fullCast.length === 0 ? styles.castingCardSelected : ''}`}
                          onClick={handleClearCasting}
                          aria-pressed={fullCast.length === 0}
                        >
                          <div className={styles.castingImageNone}>
                            <img src="/anonyface.png" alt="" className={styles.castingNoneIcon} style={{ opacity: 0.5 }} />
                          </div>
                          <div className={styles.castingMeta}>
                            指定しない
                          </div>
                        </button>
                      </li>
                      {castingModelsQuery.isLoading ? (
                        <li className={styles.castingLoading}>俳優を取得中…</li>
                      ) : castingModelsQuery.isError ? (
                        <li className={styles.castingError}>俳優の取得に失敗しました</li>
                      ) : characterActorsForSeedance?.length === 0 ? (
                        <li className={styles.castingLoading}>キャラクター俳優が登録されていません</li>
                      ) : characterActorsForSeedance?.map((model) => {
                        const isSelected = selectedCastingIds.has(model.id);
                        const orderIdx = castingOrderById.get(model.id);
                        const personLetter = (isMultiCastingEnabled && isSelected && orderIdx !== undefined)
                          ? String.fromCharCode(65 + orderIdx)
                          : null;
                        return (
                          <li key={model.id}>
                            <button
                              type="button"
                              className={`${styles.castingCard} ${styles.castingCardActor} ${isSelected ? styles.castingCardSelected : ''}`}
                              onClick={() => handleToggleSeedanceCharacterActor(model)}
                              aria-pressed={isSelected}
                            >
                              {personLetter && (
                                <span className={styles.castingExclusiveBadge} style={{ left: 4, right: 'auto', background: 'rgba(79, 70, 229, 0.9)' }}>
                                  {`登場人物${personLetter}`}
                                </span>
                              )}
                              {model.is_exclusive && (
                                <span className={styles.castingExclusiveBadge}>専属</span>
                              )}
                              <div className={styles.castingImage}>
                                {model.face_image_public_url ? (
                                  <img src={model.face_image_public_url} alt={model.display_name} />
                                ) : model.image_url ? (
                                  <img src={model.image_url} alt={model.display_name} />
                                ) : (
                                  <div className={styles.castingImagePlaceholder} />
                                )}
                              </div>
                              <div className={styles.castingMeta}>
                                {model.display_name}
                              </div>
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                </div>
                </>
                )
              ) : (
              <>
              {isMultiCastingEnabled && (
                <div style={{ fontSize: '12px', color: 'rgba(245, 241, 230, 0.6)', marginBottom: 8 }}>
                  {isModelCapBinding
                    ? `選択中の動画モデルでは最大${castingCap}人まで選択できます（${fullCast.length}/${castingCap}）`
                    : `登場人物数に合わせて最大${castingCap}人まで選択できます（${fullCast.length}/${castingCap}）`}
                </div>
              )}
              <div className={styles.castingCarousel}>
                <div className={`${styles.castingScrollContainer} ${styles.castingScrollMagnify}`}>
                  <ul className={styles.castingGrid}>
                    {/* 「指定しない」プレースホルダー */}
                    <li>
                      <button
                        type="button"
                        className={`${styles.castingCard} ${styles.castingCardNone} ${fullCast.length === 0 ? styles.castingCardSelected : ''}`}
                        onClick={handleClearCasting}
                        aria-pressed={fullCast.length === 0}
                      >
                        <div className={styles.castingImageNone}>
                          <img src="/anonyface.png" alt="" className={styles.castingNoneIcon} style={{ opacity: 0.5 }} />
                        </div>
                        <div className={styles.castingMeta}>
                          指定しない
                        </div>
                      </button>
                    </li>
                    {castingModelsQuery.isLoading ? (
                      <li className={styles.castingLoading}>モデルを取得中…</li>
                    ) : castingModelsQuery.isError ? (
                      <li className={styles.castingError}>モデルの取得に失敗しました</li>
                    ) : (isStylized && !isSeedanceVideoModel && castingModelsForDisplay?.length === 0) ? (
                      <li className={styles.castingLoading}>選択中の映像スタイルで選べるキャラクター俳優が登録されていません</li>
                    ) : castingModelsForDisplay?.map((model) => {
                      const isSelected = selectedCastingIds.has(model.id);
                      // 複数選択時は選択順を登場人物A/B…のバッジで表示。
                      const orderIdx = castingOrderById.get(model.id);
                      const personLetter = (isMultiCastingEnabled && isSelected && orderIdx !== undefined)
                        ? String.fromCharCode(65 + orderIdx)
                        : null;
                      return (
                        <li key={model.id}>
                          <button
                            type="button"
                            className={`${styles.castingCard} ${styles.castingCardActor} ${isSelected ? styles.castingCardSelected : ''}`}
                            onClick={() => handleToggleCasting(model)}
                            aria-pressed={isSelected}
                          >
                            {personLetter && (
                              <span className={styles.castingExclusiveBadge} style={{ left: 4, right: 'auto', background: 'rgba(79, 70, 229, 0.9)' }}>
                                {`登場人物${personLetter}`}
                              </span>
                            )}
                            {model.is_exclusive && (
                              <span className={styles.castingExclusiveBadge}>専属</span>
                            )}
                            <div className={styles.castingImage}>
                              {model.face_image_public_url ? (
                                <img src={model.face_image_public_url} alt={model.display_name} />
                              ) : model.image_url ? (
                                <img src={model.image_url} alt={model.display_name} />
                              ) : (
                                <div className={styles.castingImagePlaceholder} />
                              )}
                            </div>
                            <div className={styles.castingMeta}>
                              {model.display_name}
                            </div>
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              </div>
              </>
              )}
              </div>
              <div className={styles.accordionPanelFooter} style={{ justifyContent: 'space-between' }}>
                {/* 人物を含めないモード: シナリオ生成プロンプトに「人物を登場させない」指示を差し込む。
                    商品・風景・グラフィックのみで構成したいときに使う。デフォルト OFF。 */}
                <label className="form-checkbox-label" style={{ marginLeft: 4 }}>
                  <input
                    type="checkbox"
                    checked={excludePeople}
                    onChange={(e) => setExcludePeople(e.target.checked)}
                  />
                  <span>人物・キャラクターを含めない</span>
                </label>
                <Tooltip content="閉じる">
                  <button type="button" className={styles.iconButton} onClick={() => setIsCastingSectionOpen(false)}>
                    <img src="/move_collapse_vertical.svg" alt="閉じる" style={{ width: '16px', height: '16px' }} />
                  </button>
                </Tooltip>
              </div>
            </div>
          </div>
    </>
  );
}
